import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RuleIcon from '@mui/icons-material/Rule';
import SearchIcon from '@mui/icons-material/Search';
import { ALL } from './varianceSummaryTableModel.js';

const optionLabel = (option) => `${option.label} (${option.count})`;

const SelectControl = ({ id, label, value, options, allLabel, onChange }) => (
  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 132 } }}>
    <InputLabel id={`${id}-label`}>{label}</InputLabel>
    <Select
      labelId={`${id}-label`}
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <MenuItem value={ALL}>{allLabel}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>{optionLabel(option)}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const IconActionButton = ({ label, tooltip, onClick, disabled = false, children }) => (
  <Tooltip title={tooltip || label}>
    <span>
      <Button
        aria-label={label}
        variant="outlined"
        color="primary"
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{ minWidth: 36, width: 36, px: 0 }}
      >
        {children}
      </Button>
    </span>
  </Tooltip>
);

const VarianceSummaryControls = ({
  status,
  driver,
  enrollment,
  groupBy,
  savedView,
  options,
  shownCount,
  totalCount,
  search,
  exportDisabled,
  onStatusChange,
  onDriverChange,
  onEnrollmentChange,
  onGroupByChange,
  onSavedViewChange,
  onSearchChange,
  onExport,
  onRefreshAll,
  onRefreshReasons,
  onReset,
  onResetVarianceReviews,
  resetVarianceReviewsDisabled = false,
  resetVarianceReviewsBusy = false,
  selectedVarianceCount = 0,
}) => (
  <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1} alignItems={{ xs: 'stretch', xl: 'center' }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 96 }}>
      {shownCount.toLocaleString()} / {totalCount.toLocaleString()}
    </Typography>
    <SelectControl
      id="variance-summary-saved-view"
      label="View"
      value={savedView}
      options={options.savedViews || []}
      allLabel="All open work"
      onChange={onSavedViewChange}
    />
    <SelectControl
      id="variance-summary-status"
      label="Status"
      value={status}
      options={options.statuses}
      allLabel="All statuses"
      onChange={onStatusChange}
    />
    <SelectControl
      id="variance-summary-driver"
      label="Driver"
      value={driver}
      options={options.drivers}
      allLabel="All drivers"
      onChange={onDriverChange}
    />
    <SelectControl
      id="variance-summary-enrollment"
      label="DPE enrollment"
      value={enrollment}
      options={options.enrollments}
      allLabel="All enrollment"
      onChange={onEnrollmentChange}
    />
    <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 132 } }}>
      <InputLabel id="variance-summary-group-label">Group</InputLabel>
      <Select
        labelId="variance-summary-group-label"
        label="Group"
        value={groupBy}
        onChange={(event) => onGroupByChange(event.target.value)}
      >
        <MenuItem value="none">No grouping</MenuItem>
        <MenuItem value="status">Status</MenuItem>
        <MenuItem value="driver">Driver</MenuItem>
        <MenuItem value="enrollment">DPE enrollment</MenuItem>
        <MenuItem value="brand">Brand</MenuItem>
      </Select>
    </FormControl>
    <Box sx={{ flex: 1 }} />
    <IconActionButton label="Reset table filters" onClick={onReset}>
      <FilterAltOffIcon fontSize="small" />
    </IconActionButton>
    <IconActionButton
      label="Reset reviews"
      tooltip={selectedVarianceCount > 0
        ? `Reset ${selectedVarianceCount.toLocaleString()} selected review${selectedVarianceCount === 1 ? '' : 's'}`
        : 'Reset all reviewed variances in this round'}
      onClick={onResetVarianceReviews}
      disabled={!onResetVarianceReviews || resetVarianceReviewsDisabled || resetVarianceReviewsBusy}
    >
      <RestartAltIcon fontSize="small" />
    </IconActionButton>
    <IconActionButton label="Export CSV" tooltip="Export filtered results to CSV" onClick={onExport} disabled={exportDisabled}>
      <DownloadIcon fontSize="small" />
    </IconActionButton>
    <IconActionButton label="Refresh all" onClick={onRefreshAll}>
      <RefreshIcon fontSize="small" />
    </IconActionButton>
    <IconActionButton label="Refresh variance reasons" onClick={onRefreshReasons} disabled={!onRefreshReasons}>
      <RuleIcon fontSize="small" />
    </IconActionButton>
    <TextField
      size="small"
      placeholder="Search BAC, dealer, brand..."
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
      sx={{ minWidth: { xs: '100%', md: 260 } }}
      InputProps={{
        startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.disabled', mr: 1 }} />,
      }}
    />
  </Stack>
);

export default VarianceSummaryControls;
